import { useTranslation } from '@crew/modules/dist/i18n'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { CrewButtonGroup } from 'components/elements/crewButtonGroup/crewButtonGroup'
import { MinutesDisplayMode } from 'enums/app'
import { FC, memo, useCallback, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'states/hooks'
import { useEventDetailMeetingMinutesContext } from '../eventDetailMeetingMinutes'
import { setSelectedMinutesDisplayMode } from 'features/event/components/eventDetailPage/states/eventDetailPageSlice'
import {
  useCreateMeetingMinutesAutomaticMutation,
  useLazyDownloadMeetingMinutesDocumentQuery,
} from '@crew/apis/event/eventApis'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useParams } from 'react-router-dom'
import { useToast } from 'hooks/useToast'
import { downloadFileFromUrl } from 'utils'
import BaselineDelete from '~icons/ic/baseline-delete'
import { useModal } from 'components/layouts/modal/useModal'
import { CrewConfirmDialog } from 'components/elements/crewConfirmDialog/crewConfirmDialog'
import { useEventDetailMeetingMinutesToolbar } from './useEventDetailMeetingMinutesToolbar'
import { useEventDetailPageContext } from 'features/event/components/eventDetailPage/useEventDetailPage'
import { EventRecordingTranscribesBulkUpdateSpeakersDialog } from 'features/event/components/eventRecordingTranscribesBulkUpdateSpeakersDialog/eventRecordingTranscribesBulkUpdateSpeakersDialog'
import { MeetingMinutesGenerateError } from '@crew/enums/domain'

type EventDetailMeetingMinutesToolbarProps = {
  // 録画ファイルが1件でも存在するかどうか
  // 議事録生成ボタンが押下可能かどうか
  hasRecordingFiles: boolean
}

type ButtonGroupInfo = {
  key: string
  text: string | undefined
}

export const EventDetailMeetingMinutesToolbar: FC<EventDetailMeetingMinutesToolbarProps> =
  memo((props) => {
    const { eventId } = useParams()

    // 話者一括変更ダイアログで使用するプロジェクトID
    const projectId = useAppSelector((state) => state.eventDetailPage.projectId)

    // この処理が流れる際、eventIdが存在しない場合は正しく描画できないはずなのでエラーを投げる
    // （以降の処理の型制約上もこちらの方が都合が良いはず）
    if (!eventId) {
      throw new Error('eventId is undefined')
    }

    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const {
      isEditTranscribe,
      setIsEditMinutes,
      generatingMinutes,
      generateMeetingMinutesError,
    } = useEventDetailMeetingMinutesContext()
    const [showApiErrors] = useShowApiErrors()
    const toast = useToast()

    const { deleteEventRecordingTranscribes, isLoadingDeleteTranscribes } =
      useEventDetailMeetingMinutesToolbar(eventId)

    // 議事録のダウンロードAPI
    const [lazyDownloadMeetingMinutesDocumentQuery] =
      useLazyDownloadMeetingMinutesDocumentQuery()
    // 議事録生成API
    const [
      createMeetingMinutesAutomaticMutation,
      { isLoading: isLoadingCreateMeetingMinutesAutomatic },
    ] = useCreateMeetingMinutesAutomaticMutation()

    // 選択中の表示形式
    const selectedDisplayMode = useAppSelector(
      (state) => state.eventDetailPage.selectedMinutesDisplayMode
    )

    // 表示形式のボタングループ
    const displayModeButtonGroup: ButtonGroupInfo[] = useMemo(() => {
      const items = Object.keys(MinutesDisplayMode).map((key) => {
        const item = MinutesDisplayMode[key as keyof typeof MinutesDisplayMode]
        return { key: item.value, text: t(item.text) }
      })

      return items
    }, [t])

    // 表示形式の切替ボタンクリック時
    const handleDisplayModeItemClick = useCallback(
      (itemData: ButtonGroupInfo) => {
        // 表示形式を更新
        dispatch(setSelectedMinutesDisplayMode(itemData.key))
      },
      [dispatch]
    )

    // Event handle when Edit meeting minutes button is clicked
    const handleEditMeetingMinutesButtonClick = useCallback(() => {
      setIsEditMinutes(true)
    }, [setIsEditMinutes])

    // 議事録生成API実行
    const createMeetingMinutesAutomatic = useCallback(async () => {
      try {
        // 議事録生成API実行
        await createMeetingMinutesAutomaticMutation({
          eventId: eventId,
        }).unwrap()

        toast.success(t('message.event.generateMeetingMinutesStarted'))
      } catch (err) {
        showApiErrors(err)
      }
    }, [
      createMeetingMinutesAutomaticMutation,
      eventId,
      showApiErrors,
      t,
      toast,
    ])

    // 議事録をAI生成ボタン押下時
    const handleGenerateMeetingMinutesButtonClick = useCallback(() => {
      // 議事録生成API実行
      createMeetingMinutesAutomatic()
    }, [createMeetingMinutesAutomatic])

    // ダウンロードボタン押下時
    const handleDownloadButtonClick = useCallback(async () => {
      try {
        // ダウンロードAPI実行
        const response = await lazyDownloadMeetingMinutesDocumentQuery({
          eventId: eventId,
        }).unwrap()

        // 署名付きURLを使用してファイルをダウンロード
        downloadFileFromUrl(response.url, response.fileName)
      } catch (error) {
        showApiErrors(error)
      }
    }, [eventId, lazyDownloadMeetingMinutesDocumentQuery, showApiErrors])

    // 議事録を再生成ボタン押下時
    const handleRegenerateMeetingMinutesButtonClick = useCallback(() => {
      // 議事録生成API実行
      createMeetingMinutesAutomatic()
    }, [createMeetingMinutesAutomatic])

    // 話者一括変更ダイアログ
    const [
      isEventRecordingTranscribesBulkUpdateSpeakersDialogOpen,
      openEventRecordingTranscribesBulkUpdateSpeakersDialog,
      closeEventRecordingTranscribesBulkUpdateSpeakersDialog,
    ] = useModal()

    // 削除確認ダイアログ
    const [
      isBulkDeleteConfirmDialogOpen,
      openBulkDeleteConfirmDialog,
      closeBulkDeleteConfirmDialog,
    ] = useModal()

    // 確認ダイアログメッセージ
    const [bulkDeleteConfirmMessage, setBulkDeleteConfirmMessage] = useState('')

    // 選択された文字起こしリスト
    const {
      selectedEventRecordingTranscribes,
      setSelectedEventRecordingTranscribes,
    } = useEventDetailPageContext()

    // 「話者変更」ボタンクリック時
    const handleBulkUpdateSpeakersButtonClick = useCallback(() => {
      // 話者一括変更ダイアログを開く
      openEventRecordingTranscribesBulkUpdateSpeakersDialog()
    }, [openEventRecordingTranscribesBulkUpdateSpeakersDialog])

    // 文字起こし一括削除ボタンクリック時
    const handleBulkDeleteTranscribesButtonClick = useCallback(() => {
      // 確認ダイアログを表示し、削除処理を実行
      console.warn(
        'selectedEventRecordingTranscribes:',
        selectedEventRecordingTranscribes
      )
      // 確認ダイアログの表示（処理は確認ダイアログのOKボタン押下時に行う）
      setBulkDeleteConfirmMessage(t('message.general.confirmMessage.delete'))
      openBulkDeleteConfirmDialog()
    }, [openBulkDeleteConfirmDialog, selectedEventRecordingTranscribes, t])

    // 話者一括変更ダイアログの登録処理実行時
    const handleEventRecordingTranscribesBulkUpdateSpeakerDialogSubmit =
      useCallback(() => {
        // 登録処理後、選択状態をクリアする
        setSelectedEventRecordingTranscribes([])
      }, [setSelectedEventRecordingTranscribes])

    // 話者一括変更ダイアログのクローズ時
    const handleEventRecordingTranscribesBulkUpdateSpeakerDialogClose =
      useCallback(() => {
        // 話者一括変更ダイアログを閉じる
        closeEventRecordingTranscribesBulkUpdateSpeakersDialog()
      }, [closeEventRecordingTranscribesBulkUpdateSpeakersDialog])

    // 削除確認ダイアログの許可ボタンクリック時
    const handleBulkDeletePermitButtonClick = useCallback(async () => {
      try {
        // 文字起こし削除APIを実行
        await deleteEventRecordingTranscribes(selectedEventRecordingTranscribes)
        toast.success(
          t('message.eventRecordingTranscribe.eventRecordingTranscribeDeleted')
        )

        // 削除完了後、選択状態をクリアする
        setSelectedEventRecordingTranscribes([])
      } catch (err) {
        showApiErrors(err)
      }
      // 確認ダイアログを閉じる
      closeBulkDeleteConfirmDialog()
    }, [
      closeBulkDeleteConfirmDialog,
      deleteEventRecordingTranscribes,
      selectedEventRecordingTranscribes,
      setSelectedEventRecordingTranscribes,
      showApiErrors,
      t,
      toast,
    ])

    return (
      <>
        <div className=" flex flex-col gap-y-5 p-2">
          <div className="flex flex-row gap-x-1 justify-between">
            {/* 表示形式の切替ボタン */}
            <CrewButtonGroup
              items={displayModeButtonGroup}
              keyExpr="key"
              textExpr="text"
              stylingMode="text"
              selectedItemKey={selectedDisplayMode}
              onItemClick={handleDisplayModeItemClick}
              size="md"
            />

            {/* 議事録自動生成時のエラー表示 */}
            {generateMeetingMinutesError && (
              <div className="flex items-center text-red-500">
                {generateMeetingMinutesError ===
                MeetingMinutesGenerateError.TokenLimitExceeded
                  ? t(
                      'message.event.generateMeetingMinutesTokenLimitExceededError'
                    )
                  : t('message.event.generateMeetingMinutesGeneralError')}
              </div>
            )}

            <div className="flex gap-x-3 items-center ml-auto">
              {/* 議事録を選択時 */}
              {selectedDisplayMode === MinutesDisplayMode.Minutes.value && (
                <>
                  {/* 議事録をAI生成ボタン */}
                  <CrewButton
                    id="event-detail-meeting-minutes-toolbar-generate-meeting-minutes-button"
                    text={t('action.generateMinutes')}
                    onClick={handleGenerateMeetingMinutesButtonClick}
                    type="primary"
                    disabled={
                      !props.hasRecordingFiles ||
                      generatingMinutes ||
                      isLoadingCreateMeetingMinutesAutomatic
                    }
                  />

                  {/* ダウンロードボタン */}
                  <CrewButton
                    id="event-detail-meeting-minutes-toolbar-download-button"
                    text={t('action.download')}
                    onClick={handleDownloadButtonClick}
                    stylingMode="outlined"
                    disabled={generatingMinutes}
                  />

                  {/* 編集ボタン */}
                  <CrewButton
                    id="event-detail-meeting-minutes-toolbar-edit-button"
                    text={t('action.edit')}
                    onClick={handleEditMeetingMinutesButtonClick}
                    stylingMode="outlined"
                    disabled={generatingMinutes}
                  />
                </>
              )}

              {/* 文字起こしを選択時 */}
              {selectedDisplayMode ===
                MinutesDisplayMode.Transcription.value && (
                <>
                  {/* 議事録を再生成ボタン */}
                  <CrewButton
                    id="event-detail-meeting-minutes-toolbar-regenerate-meeting-minutes-button"
                    text={t('action.regenerateMinutes')}
                    onClick={handleRegenerateMeetingMinutesButtonClick}
                    type="primary"
                    disabled={
                      !props.hasRecordingFiles ||
                      generatingMinutes ||
                      isLoadingCreateMeetingMinutesAutomatic
                    }
                  />

                  {/* 話者一括変更ボタン */}
                  <CrewButton
                    id="event-detail-meeting-minutes-toolbar-bulk-update-speakers-button"
                    text={t(
                      'action.bulkUpdateEventRecordingTranscribesSpeaker'
                    )}
                    onClick={handleBulkUpdateSpeakersButtonClick}
                    stylingMode="outlined"
                    disabled={
                      selectedEventRecordingTranscribes.length === 0 ||
                      isEditTranscribe
                    } // 「文字起こし行が未選択」または「行が編集中」の場合は非活性
                  />

                  {/* 文字起こし一括削除ボタン */}
                  <CrewButton
                    id="event-detail-meeting-minutes-toolbar-bulk-delete-transcribes-button"
                    onClick={handleBulkDeleteTranscribesButtonClick}
                    stylingMode="outlined"
                    icon={<BaselineDelete width={20} height={20} />}
                    disabled={selectedEventRecordingTranscribes.length === 0} // 文字起こし行が未選択の場合は非活性
                  />
                </>
              )}
            </div>
          </div>
        </div>
        {/* 話者一括変更ダイアログ */}
        <EventRecordingTranscribesBulkUpdateSpeakersDialog
          title={t('label.bulkUpdateSpeakersDialog.title')}
          isOpen={isEventRecordingTranscribesBulkUpdateSpeakersDialogOpen}
          onSubmit={
            handleEventRecordingTranscribesBulkUpdateSpeakerDialogSubmit
          }
          onClose={handleEventRecordingTranscribesBulkUpdateSpeakerDialogClose}
          selectedTranscribes={selectedEventRecordingTranscribes}
          projectId={projectId}
        />
        {/* 削除確認ダイアログ */}
        <CrewConfirmDialog
          isOpen={isBulkDeleteConfirmDialogOpen}
          message={bulkDeleteConfirmMessage}
          onPermitButtonClick={handleBulkDeletePermitButtonClick}
          onCancelButtonClick={closeBulkDeleteConfirmDialog}
          permitButtonDisabled={isLoadingDeleteTranscribes}
        />
      </>
    )
  })
